import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import * as timeago from 'timeago.js';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DevicesIcon from '@mui/icons-material/Devices';
import DescriptionIcon from '@mui/icons-material/Description';
import LoginIcon from '@mui/icons-material/Login';

import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';

import { db } from '../store/api/fbConfig';

const API_URL = 'https://api.iot.spried.com/connect';

const BLACK = '#000000';
const WHITE = '#ffffff';
const SOFT = '#f6f6f6';
const BORDER = '#dedede';
const DARK_BORDER = '#cfcfcf';

const seoConfig = {
  title: 'Spried IoT - Simple IoT Device API and Dashboard',
  description:
    'Spried IoT is a simple black and white IoT platform for connecting devices, posting JSON data, viewing dashboards and managing device logs.',
  canonical: 'https://iot.spried.com/',
  image: 'https://iot.spried.com/images/og-image.png',
};

const examples = [
  {
    key: 'arduino',
    label: 'Arduino ESP32',
    language: 'cpp',
    fileName: 'spried-arduino-esp32.ino',
    path: '/examples/spried-arduino-esp32.ino',
  },
  {
    key: 'python',
    label: 'Python',
    language: 'python',
    fileName: 'spried-python.py',
    path: '/examples/spried-python.py',
  },
  {
    key: 'node',
    label: 'Node.js',
    language: 'js',
    fileName: 'spried-node.js',
    path: '/examples/spried-node.js',
  },
  {
    key: 'curl',
    label: 'curl',
    language: 'bash',
    fileName: 'spried-curl.sh',
    path: '/examples/spried-curl.sh',
  },
  {
    key: 'batch',
    label: 'Batch',
    language: 'bat',
    fileName: 'spried-windows.bat',
    path: '/examples/spried-windows.bat',
  },
  {
    key: 'powershell',
    label: 'PowerShell',
    language: 'ps1',
    fileName: 'spried-powershell.ps1',
    path: '/examples/spried-powershell.ps1',
  },
  {
    key: 'browser',
    label: 'Browser fetch',
    language: 'js',
    fileName: 'spried-browser-fetch.js',
    path: '/examples/spried-browser-fetch.js',
  },
];

const publicFeatures = [
  'Post JSON from ESP32, Arduino, Python, Node.js, curl or batch scripts.',
  'Create devices and keep each one protected by its own ID and key.',
  'Turn incoming data into dashboards, widgets, charts and logs.',
  'Use one simple HTTP endpoint while your hardware and firmware evolve.',
];

const deviceHeadCells = [
  { id: 'name', label: 'Name' },
  { id: 'dateLastEntries', label: 'Last Entry' },
  { id: 'entries', label: 'Entries' },
];

function setMetaTag(name, content, attribute = 'name') {
  if (!content || typeof document === 'undefined') return;

  let tag = document.head.querySelector(`meta[${attribute}="${name}"]`);

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, name);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
}

function setCanonical(url) {
  if (!url || typeof document === 'undefined') return;

  let tag = document.head.querySelector('link[rel="canonical"]');

  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }

  tag.setAttribute('href', url);
}

function setStructuredData() {
  if (typeof document === 'undefined') return;

  const existing = document.getElementById('spried-iot-home-schema');
  if (existing) existing.remove();

  const script = document.createElement('script');
  script.id = 'spried-iot-home-schema';
  script.type = 'application/ld+json';
  script.text = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Spried IoT',
    applicationCategory: 'DeveloperApplication',
    operatingSystem: 'Web',
    url: seoConfig.canonical,
    description: seoConfig.description,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'AUD',
    },
    featureList: [
      'IoT device dashboard',
      'HTTP JSON API',
      'Device logs',
      'Charts and widgets',
      'Remote controls',
    ],
  });

  document.head.appendChild(script);
}

function useLandingSeo() {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    document.title = seoConfig.title;

    setMetaTag('description', seoConfig.description);
    setMetaTag('robots', 'index, follow');
    setMetaTag(
      'keywords',
      'IoT platform, ESP32 dashboard, Arduino IoT API, device monitoring, sensor dashboard, Spried IoT'
    );

    setMetaTag('og:title', seoConfig.title, 'property');
    setMetaTag('og:description', seoConfig.description, 'property');
    setMetaTag('og:type', 'website', 'property');
    setMetaTag('og:url', seoConfig.canonical, 'property');
    setMetaTag('og:image', seoConfig.image, 'property');

    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', seoConfig.title);
    setMetaTag('twitter:description', seoConfig.description);
    setMetaTag('twitter:image', seoConfig.image);

    setCanonical(seoConfig.canonical);
    setStructuredData();
  }, []);
}

function useAuthUser() {
  const auth = getAuth();
  const [user, setUser] = useState(auth.currentUser);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
    });

    return () => unsubscribe();
  }, [auth]);

  return user;
}

function getDateMs(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;

  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatDeviceDate(value, fallback = '') {
  const ms = getDateMs(value);
  if (!ms) return fallback;

  return new Intl.DateTimeFormat('en-AU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(ms));
}

function formatDeviceTimeAgo(value, fallback = '') {
  const ms = getDateMs(value);
  if (!ms) return fallback;

  return timeago.format(ms);
}

function formatEntryCount(value) {
  const val = Number(value || 0);

  if (val > 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
  if (val > 1_000) return `${(val / 1_000).toFixed(1)}K`;

  return val;
}

function getDeviceTitle(device) {
  return device?.name || device?.DeviceId || device?.deviceId || device?.id || 'Unnamed device';
}

function getDeviceId(device) {
  return device?.DeviceId || device?.deviceId || device?.id || '';
}

function compareDeviceValues(a, b, field) {
  if (field === 'dateLastEntries' || field === 'dateCreated') {
    return getDateMs(a[field]) - getDateMs(b[field]);
  }

  if (field === 'entries') {
    return Number(a.entries || 0) - Number(b.entries || 0);
  }

  const aValue = field === 'DeviceId' ? getDeviceId(a) : a[field];
  const bValue = field === 'DeviceId' ? getDeviceId(b) : b[field];

  return String(aValue || '').localeCompare(String(bValue || ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

function useHomeDevices(user) {
  const [state, setState] = useState({
    loading: false,
    items: [],
    error: '',
  });

  useEffect(() => {
    if (!user?.uid) {
      setState({ loading: false, items: [], error: '' });
      return undefined;
    }

    let active = true;

    async function loadDevices() {
      setState((prev) => ({ ...prev, loading: true, error: '' }));

      try {
        const deviceRef = collection(db, 'Devices');

        const queries = [
          query(deviceRef, where('owner', '==', user.uid), limit(50)),
          query(deviceRef, where(`users.${user.uid}`, 'in', ['viewer', 'editor', 'admin']), limit(50)),
        ];

        const results = await Promise.allSettled(queries.map((item) => getDocs(item)));

        if (!active) return;

        const map = new Map();
        let hadSuccess = false;
        let errorMessage = '';

        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            hadSuccess = true;

            result.value.docs.forEach((row) => {
              map.set(row.id, {
                id: row.id,
                ...row.data(),
              });
            });
          } else {
            errorMessage = result.reason?.message || 'Could not load devices.';
          }
        });

        if (!hadSuccess && errorMessage) {
          throw new Error(errorMessage);
        }

        const items = Array.from(map.values()).sort(
          (a, b) => getDateMs(b.dateLastEntries) - getDateMs(a.dateLastEntries)
        );

        setState({
          loading: false,
          items,
          error: '',
        });
      } catch (error) {
        if (!active) return;

        setState({
          loading: false,
          items: [],
          error: error?.message || 'Could not load devices.',
        });
      }
    }

    loadDevices();

    return () => {
      active = false;
    };
  }, [user?.uid]);

  return state;
}

function ActionButton({ children, to, href, variant = 'contained', startIcon }) {
  const buttonProps = to ? { component: NavLink, to } : { component: 'a', href };

  return (
    <Button
      {...buttonProps}
      variant={variant}
      startIcon={startIcon}
      sx={{
        borderRadius: 0,
        px: 2.5,
        py: 1.25,
        fontWeight: 800,
        textTransform: 'none',
        bgcolor: variant === 'contained' ? BLACK : WHITE,
        color: variant === 'contained' ? WHITE : BLACK,
        borderColor: variant === 'contained' ? BLACK : DARK_BORDER,
        border: '1px solid',
        boxShadow: 'none',
        '&:hover': {
          bgcolor: variant === 'contained' ? '#222' : SOFT,
          borderColor: variant === 'contained' ? BLACK : DARK_BORDER,
          boxShadow: 'none',
        },
      }}
    >
      {children}
    </Button>
  );
}

function CodeExampleTabs({ compact = false }) {
  const [tab, setTab] = useState(0);
  const [copied, setCopied] = useState(false);
  const [exampleCache, setExampleCache] = useState({});

  const example = examples[tab];
  const loadedExample = exampleCache[example.key];
  const code = loadedExample?.code || '';
  const status = loadedExample?.status || 'idle';

  useEffect(() => {
    let active = true;

    setCopied(false);

    setExampleCache((prev) => {
      if (prev[example.key]) return prev;

      return {
        ...prev,
        [example.key]: {
          status: 'loading',
          code: '',
        },
      };
    });

    fetch(example.path)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Could not load ${example.fileName}`);
        }

        return response.text();
      })
      .then((text) => {
        if (!active) return;

        setExampleCache((prev) => ({
          ...prev,
          [example.key]: {
            status: 'loaded',
            code: text,
          },
        }));
      })
      .catch((error) => {
        if (!active) return;

        setExampleCache((prev) => ({
          ...prev,
          [example.key]: {
            status: 'error',
            code: `Example file not found: ${example.path}\n\nPlace ${example.fileName} in public/examples/.\n\n${error.message}`,
          },
        }));
      });

    return () => {
      active = false;
    };
  }, [example.key, example.path, example.fileName]);

  const copyCode = async () => {
    if (!code || !navigator?.clipboard) return;

    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch (error) {
      setCopied(false);
    }
  };

  return (
    <Paper
      id="examples"
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: BORDER,
        borderRadius: 0,
        overflow: 'hidden',
        bgcolor: WHITE,
      }}
    >
      <Box sx={{ borderBottom: '1px solid', borderColor: BORDER }}>
        <Tabs
          value={tab}
          onChange={(event, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="API example code tabs"
          sx={{
            minHeight: 44,
            '& .MuiTab-root': {
              color: BLACK,
              minHeight: 44,
              textTransform: 'none',
              fontWeight: 800,
            },
            '& .Mui-selected': {
              color: BLACK,
            },
            '& .MuiTabs-indicator': {
              bgcolor: BLACK,
            },
          }}
        >
          {examples.map((item) => (
            <Tab key={item.key} label={item.label} />
          ))}
        </Tabs>
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: BORDER }}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <CodeIcon fontSize="small" />

          <Typography sx={{ fontWeight: 900 }}>{example.fileName}</Typography>

          <Chip
            size="small"
            label={status === 'loading' ? 'loading' : example.language}
            variant="outlined"
            sx={{ borderColor: BORDER, color: BLACK, borderRadius: 0 }}
          />
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            component="a"
            href={example.path}
            target="_blank"
            rel="noreferrer"
            size="small"
            variant="outlined"
            sx={{
              borderRadius: 0,
              color: BLACK,
              borderColor: DARK_BORDER,
              textTransform: 'none',
              fontWeight: 800,
            }}
          >
            Open file
          </Button>

          <Button
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={copyCode}
            disabled={!code || status === 'loading'}
            variant="contained"
            sx={{
              borderRadius: 0,
              bgcolor: BLACK,
              color: WHITE,
              textTransform: 'none',
              fontWeight: 800,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#222', boxShadow: 'none' },
            }}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </Stack>
      </Stack>

      <Box
        component="pre"
        sx={{
          m: 0,
          p: { xs: 2, md: compact ? 2 : 3 },
          overflow: 'auto',
          bgcolor: BLACK,
          color: WHITE,
          fontSize: { xs: 12, md: 13 },
          lineHeight: 1.6,
          fontFamily: 'Consolas, Monaco, "Courier New", monospace',
          minHeight: compact ? 260 : 390,
          maxHeight: compact ? 380 : 520,
        }}
      >
        <code>{status === 'loading' ? `Loading ${example.fileName}...` : code}</code>
      </Box>
    </Paper>
  );
}

function SectionTitle({ title, text, action }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      justifyContent="space-between"
    >
      <Box>
        <Typography component="h2" variant="h5" sx={{ fontWeight: 900, color: BLACK }}>
          {title}
        </Typography>

        {text ? (
          <Typography variant="body2" sx={{ mt: 0.5, color: '#333' }}>
            {text}
          </Typography>
        ) : null}
      </Box>

      {action}
    </Stack>
  );
}

function HomeDeviceTable({ user }) {
  const navigate = useNavigate();
  const { items, loading, error } = useHomeDevices(user);

  const [orderBy, setOrderBy] = useState('dateLastEntries');
  const [order, setOrder] = useState('desc');

  const sortedDevices = useMemo(() => {
    return [...items].sort((a, b) => {
      const result = compareDeviceValues(a, b, orderBy);
      return order === 'asc' ? result : -result;
    });
  }, [items, order, orderBy]);

  const visibleDevices = sortedDevices.slice(0, 10);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const openDevice = (deviceId) => {
    if (!deviceId) return;
    navigate(`/devices/view/${deviceId}`);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: BORDER,
        borderRadius: 0,
        bgcolor: WHITE,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        sx={{
          p: { xs: 2, md: 2.5 },
          borderBottom: '1px solid',
          borderColor: BORDER,
        }}
      >
        <SectionTitle
          title="Recent device activity"
          text="Latest devices and entries from devices you can access."
          action={
            <ActionButton to="/devices" variant="outlined" startIcon={<DevicesIcon />}>
              View all
            </ActionButton>
          }
        />
      </Stack>

      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: BORDER,
          bgcolor: WHITE,
        }}
      >
        <Typography variant="body2" sx={{ color: BLACK, opacity: 0.72 }}>
          {visibleDevices.length} shown, {items.length} total
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ color: '#333' }}>
            Loading devices...
          </Typography>
        </Box>
      ) : null}

      {!loading && error ? (
        <Box sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ color: '#333' }}>
            Devices could not be loaded here. Open the devices page to view them.
          </Typography>
        </Box>
      ) : null}

      {!loading && !error ? (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="medium" sx={{ minWidth: 760 }}>
            <TableHead>
              <TableRow>
                {deviceHeadCells.map((cell) => (
                  <TableCell
                    key={cell.id}
                    sortDirection={orderBy === cell.id ? order : false}
                    sx={{
                      color: BLACK,
                      fontWeight: 900,
                      borderBottom: '1px solid',
                      borderColor: BORDER,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === cell.id}
                      direction={orderBy === cell.id ? order : 'asc'}
                      onClick={() => handleRequestSort(cell.id)}
                      sx={{
                        color: `${BLACK} !important`,
                        '& .MuiTableSortLabel-icon': {
                          color: `${BLACK} !important`,
                        },
                      }}
                    >
                      {cell.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {visibleDevices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={deviceHeadCells.length}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      sx={{ py: 2 }}
                    >
                      <Typography variant="body2" sx={{ color: '#333' }}>
                        No devices found yet.
                      </Typography>

                      <ActionButton to="/devices/add" startIcon={<AddCircleOutlineIcon />}>
                        Add device
                      </ActionButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                visibleDevices.map((device) => {
const lastEntry = formatDeviceDate(device.dateLastEntries, 'No entries');
const lastEntryAgo = formatDeviceTimeAgo(device.dateLastEntries, '');
                  const entryCount = formatEntryCount(device.entries);

                  return (
                    <TableRow
                      key={device.id}
                      hover
                      onClick={() => openDevice(device.id)}
                      tabIndex={0}
                      role="button"
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openDevice(device.id);
                        }
                      }}
                      sx={{
                        cursor: 'pointer',
                        '&:last-child td': {
                          borderBottom: 0,
                        },
                        '&:hover': {
                          bgcolor: SOFT,
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          color: BLACK,
                          borderBottom: '1px solid',
                          borderColor: BORDER,
                          pl: { xs: 2, md: 3 },
                          fontWeight: 800,
                        }}
                      >
                        <Box
                          component={NavLink}
                          to={`/devices/view/${device.id}`}
                          onClick={(event) => event.stopPropagation()}
                          sx={{
                            color: BLACK,
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          {getDeviceTitle(device)}
                        </Box>
                      </TableCell>

                      <TableCell
  sx={{
    color: BLACK,
    borderBottom: '1px solid',
    borderColor: BORDER,
    whiteSpace: 'nowrap',
  }}
>
  <Stack spacing={0.25}>
    <Typography variant="body2" sx={{ color: BLACK }}>
      {lastEntry} 
      {lastEntryAgo ? (
      <Typography variant="caption" sx={{pl:1, color: '#555' }}>
        ({lastEntryAgo})
      </Typography>
    ) : null}
    </Typography>

  </Stack>
</TableCell>

                      <TableCell
                        sx={{
                          color: BLACK,
                          borderBottom: '1px solid',
                          borderColor: BORDER,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {entryCount}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}

              {items.length > 0 ? (
                <TableRow>
                  <TableCell colSpan={deviceHeadCells.length} sx={{ borderBottom: 0, p: 0 }}>
                    <Button
                      component={NavLink}
                      to="/devices"
                      fullWidth
                      sx={{
                        justifyContent: 'flex-start',
                        borderRadius: 0,
                        px: { xs: 2, md: 3 },
                        py: 1.5,
                        color: BLACK,
                        textTransform: 'none',
                        fontWeight: 900,
                        borderTop: '1px solid',
                        borderColor: BORDER,
                        '&:hover': {
                          bgcolor: SOFT,
                        },
                      }}
                    >
                      View all devices
                    </Button>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </Paper>
  );
}

function LoggedInHome({ user }) {
  return (
    <main>
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 2.5 },
              border: '1px solid',
              borderColor: BORDER,
              borderRadius: 0,
              bgcolor: WHITE,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={7}>
                <Typography component="h1" sx={{ fontSize: 22, fontWeight: 900, color: BLACK }}>
                  Dashboard
                </Typography>

                <Typography variant="body2" sx={{ mt: 0.5, color: '#333' }}>
                  Devices, examples and documentation.
                </Typography>
              </Grid>

              <Grid item xs={12} md={5}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  justifyContent={{ md: 'flex-end' }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <ActionButton href="#examples" variant="outlined" startIcon={<CodeIcon />}>
                    Examples
                  </ActionButton>

                  <ActionButton to="/devices" variant="outlined" startIcon={<DevicesIcon />}>
                    Devices
                  </ActionButton>

                  <ActionButton to="/devices/add" startIcon={<AddCircleOutlineIcon />}>
                    Add device
                  </ActionButton>

                  <ActionButton to="/documents" variant="outlined" startIcon={<DescriptionIcon />}>
                    Documents
                  </ActionButton>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          <HomeDeviceTable user={user} />

          <Stack spacing={2}>
            <SectionTitle
              title="Examples"
              text="Copy one of these examples to start posting data to the Spried IoT API."
            />

            <CodeExampleTabs compact />
          </Stack>
        </Stack>
      </Container>
    </main>
  );
}

function PublicHome() {
  return (
    <main>
      <Box sx={{ bgcolor: WHITE, color: BLACK, borderBottom: '1px solid', borderColor: BLACK }}>
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack spacing={2.5} alignItems="flex-start">
                <Chip
                  label="HTTP JSON API for IoT devices"
                  sx={{ bgcolor: BLACK, color: WHITE, borderRadius: 0, fontWeight: 900 }}
                />

                <Typography
                  component="h1"
                  variant="h1"
                  sx={{
                    fontWeight: 900,
                    letterSpacing: -1.2,
                    fontSize: { xs: 42, sm: 56, md: 72 },
                    lineHeight: 0.98,
                  }}
                >
                  Simple IoT dashboards for real devices.
                </Typography>

                <Typography
                  component="p"
                  variant="h5"
                  sx={{ color: '#555', lineHeight: 1.55, maxWidth: 720 }}
                >
                  Connect ESP32, Arduino, Raspberry Pi, scripts and products to one API, then view
                  the data in dashboards.
                </Typography>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.25}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  <ActionButton to="/login" startIcon={<LoginIcon />}>
                    Log in
                  </ActionButton>

                  <ActionButton href="#examples" variant="outlined" startIcon={<CodeIcon />}>
                    View examples
                  </ActionButton>

                  <ActionButton to="/documents" variant="outlined" startIcon={<DescriptionIcon />}>
                    Documents
                  </ActionButton>
                </Stack>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 0,
                  border: '1px solid',
                  borderColor: BORDER,
                  bgcolor: WHITE,
                  color: BLACK,
                }}
              >
                <Typography variant="overline" sx={{ color: '#555' }}>
                  POST endpoint
                </Typography>

                <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
                  {API_URL}
                </Typography>

                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 2,
                    overflowX: 'auto',
                    bgcolor: BLACK,
                    color: WHITE,
                    fontSize: 13,
                    lineHeight: 1.55,
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                  }}
                >
                  <code>{`{
  "temperature": 22.4,
  "humidity": 51.2,
  "battery": 3.92,
  "rssi": -61
}`}</code>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <Grid container spacing={2}>
          {publicFeatures.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={feature}>
              <Paper
                elevation={0}
                sx={{
                  height: '100%',
                  p: 2.25,
                  border: '1px solid',
                  borderColor: BORDER,
                  borderRadius: 0,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 900, color: BLACK, mb: 1 }}>
                  0{index + 1}
                </Typography>

                <Typography sx={{ color: '#222', lineHeight: 1.6 }}>{feature}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box
        sx={{
          bgcolor: SOFT,
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: BORDER,
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography
                component="h2"
                variant="h3"
                sx={{ fontWeight: 900, color: BLACK, fontSize: { xs: 32, md: 46 } }}
              >
                Send data in minutes.
              </Typography>

              <Typography sx={{ mt: 1.5, color: '#333', lineHeight: 1.65 }}>
                Add a device, copy the ID and key, then POST JSON to the connect API. Start with one
                value and add more fields when the firmware is ready.
              </Typography>
            </Grid>

            <Grid item xs={12} md={7}>
              <Grid container spacing={1.5}>
                {['Create device', 'Copy credentials', 'Post JSON', 'Build widgets'].map(
                  (item, index) => (
                    <Grid item xs={12} sm={6} key={item}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor: BORDER,
                          borderRadius: 0,
                          bgcolor: WHITE,
                        }}
                      >
                        <Typography sx={{ fontWeight: 900, color: BLACK }}>
                          {index + 1}. {item}
                        </Typography>
                      </Paper>
                    </Grid>
                  )
                )}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <Stack spacing={2}>
          <Box>
            <Typography
              component="h2"
              variant="h3"
              sx={{ fontWeight: 900, color: BLACK, fontSize: { xs: 32, md: 46 } }}
            >
              Examples
            </Typography>

            <Typography sx={{ mt: 1, color: '#333' }}>
              These files are stored separately in <strong>public/examples</strong>, so Home.js
              stays smaller.
            </Typography>
          </Box>

          <CodeExampleTabs />
        </Stack>
      </Container>

      <Box sx={{ bgcolor: BLACK, color: WHITE }}>
        <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
          <Stack spacing={2} alignItems="flex-start">
            <Typography
              component="h2"
              variant="h3"
              sx={{ fontWeight: 900, fontSize: { xs: 32, md: 46 } }}
            >
              Ready to connect a device?
            </Typography>

            <Typography sx={{ color: '#e8e8e8', maxWidth: 760, lineHeight: 1.65 }}>
              Log in, add a device, and use the examples above to send your first payload.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <ActionButton to="/login" startIcon={<LoginIcon />}>
                Log in
              </ActionButton>

              <ActionButton to="/documents" variant="outlined" startIcon={<DescriptionIcon />}>
                Documents
              </ActionButton>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </main>
  );
}

function Home() {
  useLandingSeo();
  const user = useAuthUser();

  const content = useMemo(() => {
    if (user) return <LoggedInHome user={user} />;
    return <PublicHome />;
  }, [user]);

  return content;
}

export default compose(connect(null))(Home);